import * as React from "react";

import { useCurrentChat } from "@editor/hooks/chat/useCurrentChat";

import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useRequiredContext } from "replo-utils/react/context";

type MessagePopoverType = "inspiration" | "contextMenu" | null;

const CurrentChatContext = React.createContext<
  | (ReturnType<typeof useCurrentChat> & {
      messagePopoverType: MessagePopoverType;
      dispatchCurrentChat: (action: CurrentChatAction) => void;
      isContextMenuOpen: boolean;
    })
  | undefined
>(undefined);

type CurrentChatAction =
  | {
      type: "openContextMenu";
    }
  | {
      type: "closeContextMenu";
    }
  | {
      type: "closeInspirationMenu";
    }
  | {
      type: "openInspirationMenu";
    };

export const CurrentChatProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const result = useCurrentChat();

  const [messagePopoverType, setMessagePopoverType] = React.useState<
    "inspiration" | "contextMenu" | null
  >(null);

  const dispatchCurrentChat = (action: CurrentChatAction) => {
    return exhaustiveSwitch(
      action,
      "type",
    )({
      openContextMenu: () => {
        setMessagePopoverType("contextMenu");
      },
      closeContextMenu: () => {
        if (messagePopoverType === "contextMenu") {
          setMessagePopoverType(null);
        }
      },
      closeInspirationMenu: () => {
        if (messagePopoverType === "inspiration") {
          setMessagePopoverType(null);
        }
      },
      openInspirationMenu: () => {
        setMessagePopoverType("inspiration");
      },
    });
  };

  return (
    <CurrentChatContext.Provider
      value={{
        ...result,
        messagePopoverType,
        dispatchCurrentChat,
        isContextMenuOpen: messagePopoverType === "contextMenu",
      }}
    >
      {children}
    </CurrentChatContext.Provider>
  );
};

export const useCurrentChatContext = () => {
  return useRequiredContext(CurrentChatContext);
};
